import React, { useState, useEffect, useRef } from 'react';
import { FaPalette, FaSave, FaUserSlash, FaCrown, FaLock, FaImage, FaTrash, FaUserShield, FaHeart, FaGlobeAmericas, FaUserFriends } from 'react-icons/fa';
import { countContinents } from '../utils/continents';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { getToken } from 'firebase/app-check';
import { db, appCheck } from '../firebase';
import { getAnonymousDisplayName, getEmojiForUserId } from '../utils/emojiUtils';
import { Button } from '../components/ds/core/Button';
import { Card } from '../components/ds/core/Card';
import { Badge } from '../components/ds/core/Badge';
import { Avatar } from '../components/ds/core/Avatar';
import { Toggle } from '../components/ds/core/Toggle';
import './ProfileView.css';

// Helper function to compress image and convert to base64
const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 with compression
        const base64 = canvas.toDataURL('image/jpeg', quality);
        resolve(base64);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// PayPal Client ID (Live)
const PAYPAL_CLIENT_ID = 'AS8gJd8VX-8bMZnn3u5PXCVMjzCQ5gvNIF5K1ZY3con_ucC9Q8fp3geXwj0QsbtTCiroqsFJx1GGjlcQ';

function ProfileView({ user, skin, setSkin, moeSparkles, setMoeSparkles, setUser, setBgColor, setNavbarColor, setBgImage, setNavbarImage }) {
  const [bgColor, setLocalBgColor] = useState('#ADD8E6');
  const [earthColor, setEarthColor] = useState('#2faf68');
  const [oceanColor, setOceanColor] = useState('#145fb0');
  const [navbarColor, setLocalNavbarColor] = useState('#001F3F');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [friendsCount, setFriendsCount] = useState(0);
  const [continentsReached, setContinentsReached] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const paypalButtonRef = useRef(null);

  // Custom image states
  const [customBgImage, setCustomBgImage] = useState(null);
  const [customNavbarImage, setCustomNavbarImage] = useState(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingNavbar, setUploadingNavbar] = useState(false);
  const bgImageInputRef = useRef(null);
  const navbarImageInputRef = useRef(null);

  // Staff view mode states
  const [isStaff, setIsStaff] = useState(false);
  const [userViewMode, setUserViewMode] = useState(localStorage.getItem('staffUserViewMode') === 'true');

  useEffect(() => {
    loadUserProfile();
    loadBlockedUsers();
    loadFriendsCount();
    loadContinentsReached();
    loadCustomImages();
    checkStaffStatus();
  }, []);

  const loadFriendsCount = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      const friendshipsQuery = query(
        collection(db, 'friendships'),
        where('participants', 'array-contains', userId),
        where('status', '==', 'accepted')
      );
      const snapshot = await getDocs(friendshipsQuery);
      setFriendsCount(snapshot.size);
    } catch (err) {
      console.error('Failed to load friends count:', err);
    }
  };

  const loadContinentsReached = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      const messagesSnapshot = await getDocs(query(
        collection(db, 'messages'),
        where('receiverId', '==', userId)
      ));
      const isoCodes = messagesSnapshot.docs
        .map((d) => d.data().senderCountryCode)
        .filter(Boolean);
      setContinentsReached(countContinents(isoCodes));
    } catch (err) {
      console.error('Failed to load continents reached:', err);
    }
  };

  const checkStaffStatus = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const adminDoc = await getDoc(doc(db, 'admins', userId));
        if (adminDoc.exists()) {
          setIsStaff(true);
          return;
        }
        const workerDoc = await getDoc(doc(db, 'workers', userId));
        if (workerDoc.exists()) {
          setIsStaff(true);
        }
      }
    } catch (error) {
      console.error('Error checking staff status:', error);
    }
  };

  const toggleUserViewMode = () => {
    const newValue = !userViewMode;
    setUserViewMode(newValue);
    localStorage.setItem('staffUserViewMode', newValue.toString());
    window.dispatchEvent(new Event('staffViewModeChanged'));
  };

  const loadCustomImages = () => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const savedBgImage = localStorage.getItem(`yapper_bg_image_${userId}`);
      const savedNavbarImage = localStorage.getItem(`yapper_navbar_image_${userId}`);

      if (savedBgImage) {
        setCustomBgImage(savedBgImage);
        if (setBgImage) setBgImage(savedBgImage);
      }
      if (savedNavbarImage) {
        setCustomNavbarImage(savedNavbarImage);
        if (setNavbarImage) setNavbarImage(savedNavbarImage);
      }
    }
  };

  const loadUserProfile = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserInfo(userData);
        setIsPremium(userData.isPremium || false);
        setLocalBgColor(userData.themeBackgroundColor || '#ADD8E6');
        setEarthColor(userData.themeEarthColor || '#2faf68');
        setOceanColor(userData.themeOceanColor || '#145fb0');
        setLocalNavbarColor(userData.themeNavbarColor || '#001F3F');
      }
    } catch (err) {
      console.error('Failed to load user profile:', err);
    }
  };

  const loadBlockedUsers = async () => {
    setLoadingBlocked(true);
    try {
      const userId = localStorage.getItem('userId');
      const blocksQuery = query(
        collection(db, 'blocks'),
        where('blockerId', '==', userId)
      );

      const blocksSnapshot = await getDocs(blocksQuery);
      const blockedUsersList = [];

      for (const blockDoc of blocksSnapshot.docs) {
        const blockData = blockDoc.data();
        const blockedUserId = blockData.blockedId;

        // Fetch blocked user's info
        const userDoc = await getDoc(doc(db, 'users', blockedUserId));
        if (userDoc.exists()) {
          blockedUsersList.push({
            id: blockDoc.id,
            blockedId: blockedUserId,
            username: userDoc.data().username || 'Unknown User'
          });
        }
      }

      setBlockedUsers(blockedUsersList);
    } catch (err) {
      console.error('Failed to load blocked users:', err);
    } finally {
      setLoadingBlocked(false);
    }
  };

  const handleUnblock = async (blockId, username) => {
    try {
      await deleteDoc(doc(db, 'blocks', blockId));
      setBlockedUsers(blockedUsers.filter(user => user.id !== blockId));
      setMessage(`Unblocked ${username} successfully!`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to unblock user:', err);
      setMessage('Failed to unblock user');
    }
  };

  // Handle background image upload
  const handleBgImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file');
      return;
    }

    setUploadingBg(true);
    try {
      const compressedImage = await compressImage(file);
      const userId = localStorage.getItem('userId');

      // Save to localStorage
      localStorage.setItem(`yapper_bg_image_${userId}`, compressedImage);
      setCustomBgImage(compressedImage);
      if (setBgImage) setBgImage(compressedImage);

      setMessage('Background image uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to upload background image:', err);
      setMessage('Failed to upload image. Try a smaller image.');
    } finally {
      setUploadingBg(false);
      if (bgImageInputRef.current) bgImageInputRef.current.value = '';
    }
  };

  // Handle navbar image upload
  const handleNavbarImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file');
      return;
    }

    setUploadingNavbar(true);
    try {
      const compressedImage = await compressImage(file, 1920, 200, 0.8);
      const userId = localStorage.getItem('userId');

      // Save to localStorage
      localStorage.setItem(`yapper_navbar_image_${userId}`, compressedImage);
      setCustomNavbarImage(compressedImage);
      if (setNavbarImage) setNavbarImage(compressedImage);

      setMessage('Navbar image uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to upload navbar image:', err);
      setMessage('Failed to upload image. Try a smaller image.');
    } finally {
      setUploadingNavbar(false);
      if (navbarImageInputRef.current) navbarImageInputRef.current.value = '';
    }
  };

  // Remove background image
  const removeBgImage = () => {
    const userId = localStorage.getItem('userId');
    localStorage.removeItem(`yapper_bg_image_${userId}`);
    setCustomBgImage(null);
    if (setBgImage) setBgImage(null);
    setMessage('Background image removed');
    setTimeout(() => setMessage(''), 3000);
  };

  // Remove navbar image
  const removeNavbarImage = () => {
    const userId = localStorage.getItem('userId');
    localStorage.removeItem(`yapper_navbar_image_${userId}`);
    setCustomNavbarImage(null);
    if (setNavbarImage) setNavbarImage(null);
    setMessage('Navbar image removed');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleSelectSkin = async (newSkin) => {
    if (newSkin !== 'default' && newSkin !== 'daylight' && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    if (setSkin) setSkin(newSkin);
    try {
      const userId = localStorage.getItem('userId');
      if (userId) await updateDoc(doc(db, 'users', userId), { themeSkin: newSkin });
    } catch (err) {
      console.error('Failed to save skin:', err);
    }
  };

  const handleSaveTheme = async () => {
    // Check if user is a guest
    if (localStorage.getItem('isGuest') === 'true') {
      setMessage('Guest accounts cannot purchase premium. Create an account to unlock this feature!');
      setTimeout(() => setMessage(''), 4000);
      return;
    }

    if (!isPremium) {
      setShowPremiumModal(true);
      return;
    }

    setSaving(true);
    try {
      const userId = localStorage.getItem('userId');

      // Update user document in Firestore
      await updateDoc(doc(db, 'users', userId), {
        themeBackgroundColor: bgColor,
        themeEarthColor: earthColor,
        themeOceanColor: oceanColor,
        themeNavbarColor: navbarColor
      });

      // Update local state
      const updatedUser = {
        ...userInfo,
        themeBackgroundColor: bgColor,
        themeEarthColor: earthColor,
        themeOceanColor: oceanColor,
        themeNavbarColor: navbarColor
      };
      setUserInfo(updatedUser);
      if (setUser) setUser(updatedUser);
      if (setBgColor) setBgColor(bgColor);
      if (setNavbarColor) setNavbarColor(navbarColor);

      setMessage('Theme saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save theme:', err);
      setMessage('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  // Load PayPal SDK when premium modal opens
  useEffect(() => {
    if (showPremiumModal && !paypalLoaded && !isPremium) {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
      script.async = true;
      script.onload = () => {
        setPaypalLoaded(true);
      };
      document.body.appendChild(script);

      return () => {
        // Cleanup script on unmount
        const existingScript = document.querySelector(`script[src*="paypal.com/sdk/js"]`);
        if (existingScript) {
          document.body.removeChild(existingScript);
        }
      };
    }
  }, [showPremiumModal, paypalLoaded, isPremium]);

  // Safety net: if a charge succeeds but activation fails, record it so it can be recovered
  const recordPendingActivation = async (orderId) => {
    try {
      localStorage.setItem('yapper_pending_premium_order', orderId);
      await addDoc(collection(db, 'pendingActivations'), {
        userId: localStorage.getItem('userId') || null,
        orderId,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error('Failed to record pending activation:', e);
    }
  };

  // Render PayPal button when SDK is loaded
  useEffect(() => {
    if (paypalLoaded && showPremiumModal && paypalButtonRef.current && window.paypal) {
      // Clear any existing buttons
      paypalButtonRef.current.innerHTML = '';

      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal'
        },
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              description: 'Yapp Premium',
              amount: {
                currency_code: 'USD',
                value: '4.99'
              }
            }]
          });
        },
        onApprove: async (data, actions) => {
          setPaymentProcessing(true);
          let order = null;
          try {
            // Capture the payment
            order = await actions.order.capture();
            // Payment captured successfully

            // Call our Firebase function to activate premium
            const userId = localStorage.getItem('userId');
            const appCheckTokenResponse = await getToken(appCheck, false);
            const response = await fetch('https://us-central1-etype-final.cloudfunctions.net/verifyPayPalPayment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Firebase-AppCheck': appCheckTokenResponse.token,
              },
              body: JSON.stringify({
                orderId: order.id,
                userId: userId
              })
            });

            const result = await response.json();

            if (result.success) {
              // Update local state
              setIsPremium(true);
              setShowPremiumModal(false);
              setMessage('Premium activated! You can now customize your theme.');

              // Reload user profile to get updated data
              loadUserProfile();
            } else {
              // Charged, but activation failed - record for recovery and surface the order ID
              await recordPendingActivation(order.id);
              setShowPremiumModal(false);
              setMessage(`Your payment went through, but activation is still pending. Please contact support and include this order ID: ${order.id}`);
            }
          } catch (error) {
            console.error('Error processing payment:', error);
            if (order && order.id) {
              // Payment was captured before something failed - don't lose it
              await recordPendingActivation(order.id);
              setShowPremiumModal(false);
              setMessage(`Your payment went through, but activation is still pending. Please contact support and include this order ID: ${order.id}`);
            } else {
              setMessage('There was a problem completing your payment. If you were charged, please contact support.');
            }
          } finally {
            setPaymentProcessing(false);
          }
        },
        onError: (err) => {
          console.error('PayPal error:', err);
          setMessage('Payment failed. Please try again.');
          setPaymentProcessing(false);
        },
        onCancel: () => {
          setMessage('Payment cancelled.');
        }
      }).render(paypalButtonRef.current);
    }
  }, [paypalLoaded, showPremiumModal]);

  const displayName = userInfo?.username || localStorage.getItem('userNickname') || 'Explorer';

  return (
    <div className="profile-view">
      <div className="profile-container">

        {/* identity */}
        <Card variant="glass" padding="md" className="profile-card identity-card" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
          <Avatar name={displayName} kind="brand" size={72} />
          <div className="identity-main">
            <div className="identity-name-row">
              <span className="identity-name">{displayName}</span>
              {isPremium && (
                <Badge tone="premium" icon={<FaCrown />}>Premium</Badge>
              )}
            </div>
            <div className="identity-anon">
              <span className="anonymous-emoji">{getEmojiForUserId(localStorage.getItem('userId'))}</span>
              {getAnonymousDisplayName(localStorage.getItem('userId'))}
            </div>
            <span className="info-hint">This is how non-friends see you</span>
          </div>
        </Card>

        {/* stats */}
        <div className="stat-grid">
          <Card variant="glass" padding="sm" className="profile-card stat-tile" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <FaGlobeAmericas className="stat-icon" />
            <div className="stat-value">{continentsReached}</div>
            <div className="stat-label">continents reached</div>
          </Card>
          <Card variant="glass" padding="sm" className="profile-card stat-tile" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <FaHeart className="stat-icon" />
            <div className="stat-value">{userInfo?.likes || 0}</div>
            <div className="stat-label">likes received</div>
          </Card>
          <Card variant="glass" padding="sm" className="profile-card stat-tile" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <FaUserFriends className="stat-icon" />
            <div className="stat-value">{friendsCount}</div>
            <div className="stat-label">friends</div>
          </Card>
        </div>

        <div className="profile-content">
          <Card variant="glass" padding="md" className="profile-card profile-section" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <h3 className="section-title"><FaPalette /> Globe theme</h3>
            <div className="preset-grid">
              {[
                { name: 'Default', bg: '#0f1423', earth: '#2faf68', ocean: '#145fb0', navbar: '#001F3F' },
                { name: 'Morpheus', bg: '#000000', earth: '#2a932a', ocean: '#000000', navbar: '#000000' },
                { name: 'Sunset', bg: '#FFB6C1', earth: '#FF6347', ocean: '#2B1B3D', navbar: '#4A1C1C' },
                { name: 'Arctic', bg: '#E0FFFF', earth: '#87CEEB', ocean: '#4682B4', navbar: '#1A3A4A' },
              ].map((theme) => (
                <div key={theme.name} className="preset-theme">
                  <button
                    className="preset-btn"
                    onClick={async () => {
                      const userId = localStorage.getItem('userId');

                      // Update local state
                      setLocalBgColor(theme.bg);
                      setEarthColor(theme.earth);
                      setOceanColor(theme.ocean);
                      setLocalNavbarColor(theme.navbar);

                      // Apply changes in real-time
                      if (setBgColor) setBgColor(theme.bg);
                      if (setNavbarColor) setNavbarColor(theme.navbar);

                      // Save to database immediately
                      try {
                        await updateDoc(doc(db, 'users', userId), {
                          themeBackgroundColor: theme.bg,
                          themeEarthColor: theme.earth,
                          themeOceanColor: theme.ocean,
                          themeNavbarColor: theme.navbar
                        });

                        // Update user object
                        const updatedUser = {
                          ...userInfo,
                          themeBackgroundColor: theme.bg,
                          themeEarthColor: theme.earth,
                          themeOceanColor: theme.ocean,
                          themeNavbarColor: theme.navbar
                        };
                        setUserInfo(updatedUser);
                        if (setUser) setUser(updatedUser);

                        setMessage(`${theme.name} theme applied!`);
                        setTimeout(() => setMessage(''), 2000);
                      } catch (err) {
                        console.error('Failed to save theme:', err);
                        setMessage('Failed to save theme');
                      }
                    }}
                    title={theme.name}
                  >
                    <span className="preset-swatch">
                      <span className="preset-swatch-half" style={{ background: theme.earth }} />
                      <span className="preset-swatch-half" style={{ background: theme.ocean }} />
                    </span>
                    <span className="preset-name">{theme.name}</span>
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <Card variant="glass" padding="md" className="profile-card profile-section" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <h3 className="section-title">
              <FaPalette /> Skin
              {!isPremium && <Badge tone="premium" icon={<FaCrown />}>Premium</Badge>}
            </h3>
            <div className="skin-grid">
              {[['default', '🌙', 'Default Night', false], ['daylight', '☀️', 'Default Daylight', false], ['wired', '🟢', 'Wired', true], ['moe', '🌸', 'Moe', true], ['penpal', '✉️', 'Pen-pal', true]].map(([key, icon, label, premium]) => (
                <button
                  key={key}
                  className={`skin-btn ${(skin || 'default') === key ? 'active' : ''} ${premium && !isPremium ? 'locked' : ''}`}
                  onClick={() => handleSelectSkin(key)}
                >
                  <span className="skin-btn-icon">{icon}</span>
                  <span className="skin-btn-label">
                    {label}
                    {premium && !isPremium && <FaLock className="skin-btn-lock" />}
                  </span>
                </button>
              ))}
            </div>
            <p className="skin-note">"Default Night" and "Default Daylight" are free. Premium skins: "Wired" (retro green CRT terminal), "Moe" (pastel dream), and "Pen-pal" (vintage airmail letters).</p>

            {skin === 'moe' && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  marginTop: '0.75rem',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!moeSparkles}
                  onChange={(e) => setMoeSparkles && setMoeSparkles(e.target.checked)}
                />
                <span>✨ Floating sparkles {moeSparkles ? '(on)' : '(off)'}</span>
              </label>
            )}
          </Card>

          <Card variant="glass" padding="md" className={`profile-card profile-section ${!isPremium ? 'locked-section' : ''}`} style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <div className="section-header">
              <h3 className="section-title">
                <FaPalette /> Theme Customization
                {isPremium && <Badge tone="premium" icon={<FaCrown />}>Premium</Badge>}
              </h3>
              {!isPremium && (
                <div className="premium-lock-banner">
                  <span><FaLock /> Unlock Yapp Premium for $4.99 — custom themes, exclusive skins &amp; ad-free (Preset themes are free!)</span>
                  <Button variant="primary" icon={<FaCrown />} onClick={() => {
                    if (localStorage.getItem('isGuest') === 'true') {
                      setMessage('Guest accounts cannot purchase premium. Create an account to unlock this feature!');
                      setTimeout(() => setMessage(''), 4000);
                      return;
                    }
                    setShowPremiumModal(true);
                  }}>
                    Unlock Now
                  </Button>
                </div>
              )}
            </div>
            <div className={`theme-group ${!isPremium ? 'blurred-content' : ''}`}>
              <div className="color-input-group">
                <label htmlFor="bg-color">
                  <FaPalette /> Background Color
                </label>
                <div className="color-input-wrapper">
                  <input
                    id="bg-color"
                    type="color"
                    value={bgColor}
                    onChange={(e) => {
                      setLocalBgColor(e.target.value);
                      if (setBgColor) setBgColor(e.target.value);
                    }}
                  />
                  <input
                    type="text"
                    value={bgColor}
                    onChange={(e) => {
                      setLocalBgColor(e.target.value);
                      if (setBgColor) setBgColor(e.target.value);
                    }}
                    placeholder="#ADD8E6"
                  />
                </div>
                <div
                  className="color-preview"
                  style={{ backgroundColor: bgColor }}
                ></div>
              </div>

              <div className="color-input-group">
                <label htmlFor="earth-color">
                  <FaPalette /> Earth Color
                </label>
                <div className="color-input-wrapper">
                  <input
                    id="earth-color"
                    type="color"
                    value={earthColor}
                    onChange={(e) => setEarthColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={earthColor}
                    onChange={(e) => setEarthColor(e.target.value)}
                    placeholder="#2faf68"
                  />
                </div>
                <div
                  className="color-preview"
                  style={{ backgroundColor: earthColor }}
                ></div>
              </div>

              <div className="color-input-group">
                <label htmlFor="ocean-color">
                  <FaPalette /> Ocean Color
                </label>
                <div className="color-input-wrapper">
                  <input
                    id="ocean-color"
                    type="color"
                    value={oceanColor}
                    onChange={(e) => setOceanColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={oceanColor}
                    onChange={(e) => setOceanColor(e.target.value)}
                    placeholder="#145fb0"
                  />
                </div>
                <div
                  className="color-preview"
                  style={{ backgroundColor: oceanColor }}
                ></div>
              </div>

              <div className="color-input-group">
                <label htmlFor="navbar-color">
                  <FaPalette /> Navbar Color
                </label>
                <div className="color-input-wrapper">
                  <input
                    id="navbar-color"
                    type="color"
                    value={navbarColor}
                    onChange={(e) => {
                      setLocalNavbarColor(e.target.value);
                      if (setNavbarColor) setNavbarColor(e.target.value);
                    }}
                  />
                  <input
                    type="text"
                    value={navbarColor}
                    onChange={(e) => {
                      setLocalNavbarColor(e.target.value);
                      if (setNavbarColor) setNavbarColor(e.target.value);
                    }}
                    placeholder="#001F3F"
                  />
                </div>
                <div
                  className="color-preview"
                  style={{ backgroundColor: navbarColor }}
                ></div>
              </div>

              {/* Custom Image Uploads */}
              <div className="custom-images-section">
                <h3><FaImage /> Custom Background Images</h3>
                <p className="image-note">Images are saved locally on your device only</p>

                {/* Background Image Upload */}
                <div className="image-upload-group">
                  <label><FaImage /> Background Image</label>
                  <div className="image-upload-controls">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBgImageUpload}
                      ref={bgImageInputRef}
                      style={{ display: 'none' }}
                      id="bg-image-input"
                    />
                    <Button
                      variant="secondary"
                      icon={<FaImage />}
                      onClick={() => bgImageInputRef.current?.click()}
                      disabled={uploadingBg}
                    >
                      {uploadingBg ? 'Uploading...' : 'Choose Image'}
                    </Button>
                    {customBgImage && (
                      <Button variant="danger" icon={<FaTrash />} onClick={removeBgImage}>
                        Remove
                      </Button>
                    )}
                  </div>
                  {customBgImage && (
                    <div className="image-preview">
                      <img src={customBgImage} alt="Background preview" />
                    </div>
                  )}
                </div>

                {/* Navbar Image Upload */}
                <div className="image-upload-group">
                  <label><FaImage /> Navbar Image</label>
                  <div className="image-upload-controls">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleNavbarImageUpload}
                      ref={navbarImageInputRef}
                      style={{ display: 'none' }}
                      id="navbar-image-input"
                    />
                    <Button
                      variant="secondary"
                      icon={<FaImage />}
                      onClick={() => navbarImageInputRef.current?.click()}
                      disabled={uploadingNavbar}
                    >
                      {uploadingNavbar ? 'Uploading...' : 'Choose Image'}
                    </Button>
                    {customNavbarImage && (
                      <Button variant="danger" icon={<FaTrash />} onClick={removeNavbarImage}>
                        Remove
                      </Button>
                    )}
                  </div>
                  {customNavbarImage && (
                    <div className="image-preview navbar-preview">
                      <img src={customNavbarImage} alt="Navbar preview" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {message && (
              <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}

            <Button
              variant="primary"
              fullWidth
              icon={<FaSave />}
              onClick={handleSaveTheme}
              disabled={saving || !isPremium}
            >
              {saving ? 'Saving...' : !isPremium ? 'Unlock Premium to Save' : 'Save & Apply'}
            </Button>
          </Card>

          <Card variant="glass" padding="md" className="profile-card profile-section" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
            <h3 className="section-title"><FaUserSlash /> Blocked Users</h3>
            {loadingBlocked ? (
              <p className="loading-text">Loading blocked users...</p>
            ) : blockedUsers.length === 0 ? (
              <p className="empty-text">No blocked users</p>
            ) : (
              <div className="blocked-users-list">
                {blockedUsers.map((blockedUser) => (
                  <div key={blockedUser.id} className="blocked-user-item">
                    <span className="blocked-username">{blockedUser.username}</span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleUnblock(blockedUser.id, blockedUser.username)}
                    >
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {isStaff && (
            <Card variant="glass" padding="md" className="profile-card profile-section staff-view-section" style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}>
              <h3 className="section-title"><FaUserShield /> Staff Settings</h3>
              <div className="staff-view-toggle">
                <div className="toggle-row">
                  <div className="toggle-info">
                    <span className="toggle-label">
                      {userViewMode ? '👤 User View Mode' : '🛡️ Staff View Mode'}
                    </span>
                    <p className="toggle-description">
                      {userViewMode
                        ? 'Staff links are hidden. You see the app as a regular user.'
                        : 'All staff features are visible in the navbar.'}
                    </p>
                  </div>
                  <Toggle
                    checked={userViewMode}
                    onChange={toggleUserViewMode}
                    tone="warning"
                    aria-label="Toggle staff view mode"
                  />
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {showPremiumModal && (
        <div className="modal-overlay" onClick={() => setShowPremiumModal(false)}>
          <div className="modal-content premium-modal" onClick={(e) => e.stopPropagation()}>
            <div className="premium-modal-header">
              <FaCrown className="premium-icon" />
              <h2>Unlock Yapp Premium</h2>
            </div>

            <div className="premium-features">
              <h3>Premium Features Include:</h3>
              <ul>
                <li>Custom background, earth, ocean &amp; navbar colors</li>
                <li>Custom background &amp; navbar images</li>
                <li>Exclusive premium skins (Wired &amp; Moe)</li>
                <li>Ad-free experience</li>
                <li>Unlimited theme changes</li>
              </ul>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem', marginBottom: 0 }}>
                Note: Preset themes are available for free to all users!
              </p>
            </div>

            <div className="premium-price">
              <span className="price-amount">$4.99</span>
              <span className="price-period">One-time payment</span>
            </div>

            <div className="paypal-button-container">
              {paymentProcessing ? (
                <div className="payment-processing">
                  <div className="spinner"></div>
                  <p>Processing payment...</p>
                </div>
              ) : (
                <>
                  {!paypalLoaded && (
                    <div className="paypal-loading">
                      <div className="spinner"></div>
                      <p>Loading payment options...</p>
                    </div>
                  )}
                  <div ref={paypalButtonRef} id="paypal-button-container"></div>
                </>
              )}
            </div>

            <div className="modal-actions">
              <Button variant="ghost" onClick={() => setShowPremiumModal(false)} disabled={paymentProcessing}>
                Cancel
              </Button>
            </div>

            <p className="premium-note">
              Secure payment via PayPal. Premium activates instantly after payment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileView;
